import React, {useMemo, useRef, forwardRef, useImperativeHandle} from 'react';
import {
  PreviewAPI,
  ViewMode,
  CoreDoms,
  Viewers,
  FlowDirection,
  LayoutContext,
  EditorExperienceType,
  DEFAULT_ZOOM,
} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import classNames from 'classnames';
import {getPageName} from '../../../Utils/Common';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {Button} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';
import {useExperience} from '../../Experience/ExperienceProvider';

export interface PageProps {
  page: PageInterface;
  layout: ProductLayout;
  editorType: EditorExperienceType;
  viewMode?: ViewMode;
  gridSize?: number;
  gridRowGap?: number;
  gridColGap?: number;
  showToolbar?: boolean;
  showEmptyBoxes?: boolean;
  showErrors?: boolean;
  jumpToPage?: number;
}

export interface PreviewPageProps extends PageProps {
  onContinueInEditor?: (pageNumber?: number) => void;
}

const PreviewList = forwardRef(
  (
    {page, layout, editorType, viewMode, gridSize, onContinueInEditor}: PreviewPageProps,
    fwdRef
  ) => {
    const ref = useRef<PreviewAPI<any> | null>(null);
    const pageContainerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLDivElement>(null);

    const ui = useUiState();

    // if a fowardRef is passed in, yield it
    useImperativeHandle(fwdRef, () => ref.current);
    const {t} = useTranslation();
    const {experience} = useExperience();

    const settings = useMemo(() => {
      return {
        viewerName: Viewers.Tertiary,
        ismobile: false,
        isCanvasListening: false,
        isCalendarEvent: false,
        singlePageThumbnail: false,
        autoLayoutEnabled: false,
        zoom: DEFAULT_ZOOM,
        pageNumber: page.pagenumber,
        cache: false,
        editorType,
        components: {
          background: true,
          bleed: {
            outside: false,
            inside: false,
          },
          gutter: true,
          spine: true,
          flaps: true,
          empty: false,
          wrap: true,
          errors: false,
        },
        context: LayoutContext.TempAPI,
      };
    }, [page.pagenumber, ui.state.viewMode]);

    React.useEffect(() => {
      let previewAPI: PreviewAPI<any> = null as any;
      if (!ref.current) {
        previewAPI = new PreviewAPI(settings);
        ref.current = previewAPI;
      }

      previewAPI
        .setSpreadSize({
          pageNumber: page.pagenumber,
          rulersVisible: false,
          rulerOriginHeight: 0,
          borderSize: 0,
          left: 0,
          top: 0,
          viewMode: ViewMode.List,
          direction: FlowDirection.Column,
        })
        .display();

      return () => {
        previewAPI.destroy();

        // we may not have a stage if the API is not in the viewport
        if (previewAPI.getStage()) {
          previewAPI.setStageSize({width: 0, height: 0});
        }
        ref.current = null;
      };
    }, []);

    const id = `${CoreDoms.ViewportName}-${Viewers.Tertiary}-${page.pagenumber}`;
    const pageContainerClasses = ['relative', 'flex', 'justify-center', 'sm:snap-center'];
    const pageFooterClasses = classNames(
      'flex',
      'mt-xs',
      'lg:flex-col',
      'sm:flex-wrap',
      'relative',
      'mt-xs'
    );

    const pageNameClasses = classNames(
      'flex',
      'justify-around',
      'sm:mx-auto',
      'opacity-40',
      'text-sm'
    );

    return (
      <>
        <li>
          <div className={pageContainerClasses.join(' ')} ref={pageContainerRef}>
            <div ref={canvasRef} id={id} className={'shadow-[0_2px_5px_rgb(0_0_0_/_15%)]'}></div>
          </div>
          <div className={pageFooterClasses}>
            <p className={pageNameClasses}>{getPageName(page, layout)}</p>
          </div>
          <div className={classNames(...pageFooterClasses, 'mb-lg')}>
            <p className={pageNameClasses}>
              {onContinueInEditor && experience.WIZARD.previewStep.perPageEditButton && (
                <Button
                  buttonStyle={'tertiary'}
                  size={'small'}
                  label={t('*:str_LabelPageEdit')}
                  onClick={() => onContinueInEditor(page.pagenumber)}
                />
              )}
            </p>
          </div>
        </li>
      </>
    );
  }
);

export default PreviewList;
